/**
 * Inline follow-up to the Oracle "Browse services on this listener…" fix chip
 * (ORA-12514 / ORA-12505): probes the failing connection's host:port with the
 * existing service discovery and renders the same pick-a-service pills as the
 * connection form's "Discover" button. Picking a service hands it back to the
 * caller, which retries the connect with it (persisted only on success).
 */
import { useEffect, useState } from 'react';
import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import { DiscoveryResults } from './OracleConnectionFields';
import {
  runOracleServiceDiscovery,
  type OracleDiscoveryState,
} from '../utils/oracleDiscoveryClient';

type OracleListenerServicesBrowserProps = {
  server: string | undefined;
  port: string | number | undefined;
  /** The service name that just failed — probed alongside the candidates. */
  hint?: string;
  onPick: (service: string) => void;
};

export function OracleListenerServicesBrowser({
  server,
  port,
  hint,
  onPick,
}: OracleListenerServicesBrowserProps) {
  const [discovery, setDiscovery] = useState<OracleDiscoveryState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setDiscovery({ status: 'loading' });
    void runOracleServiceDiscovery(server, port, hint).then((result) => {
      if (!cancelled) setDiscovery(result);
    });
    return () => {
      cancelled = true;
    };
  }, [server, port, hint]);

  if (discovery.status === 'loading') {
    return (
      <div
        className="mt-2 flex items-center gap-2 text-xs text-destructive/80"
        data-testid="oracle-listener-services-loading"
      >
        <RandomDotmSpinner size={14} />
        Probing the listener for registered services…
      </div>
    );
  }

  return (
    <div data-testid="oracle-listener-services-browser">
      <DiscoveryResults state={discovery} onPick={onPick} />
    </div>
  );
}
